/**
 * The privacy policy URL is free text typed by the organization (settings, survey and campaign
 * editors, APIs), so turn it into an absolute http(s) URL or null. A scheme-less
 * "www.acme.fi/privacy" or "acme.fi/privacy" (about 1.5 % of surveys with a policy in production,
 * Oct 2026) would resolve against the form host and 404, so it gets https:// prepended. Anything
 * else, such as a javascript: URL that would run on click or a plain word, is rejected.
 *
 * The client validates input with this and the server validates every write with it, so the two
 * cannot disagree on what is storable.
 */
export function toPrivacyPolicyHref(value: string): string | null {
  const trimmed = value.trim()
  // Whitespace means prose ("see acme.fi") or a broken URL ("https:// acme.fi"). A leading slash
  // is a path or a protocol-relative URL, neither of which points at the organization's site.
  if (!trimmed || /\s/.test(trimmed) || trimmed.startsWith('/')) return null

  const hasHttpScheme = /^https?:\/\//i.test(trimmed)
  // Any other scheme (javascript:, mailto:, file:, the "https:/" typo) is rejected. So is a
  // scheme-less "acme.fi:8080", which reads as one: ports are not allowed anyway.
  if (!hasHttpScheme && /^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return null

  const href = hasHttpScheme ? trimmed : `https://${trimmed}`
  let url: URL
  try {
    url = new URL(href)
  } catch {
    // Unparseable, e.g. a bare "https://"
    return null
  }

  // An email address typed into the field ("privacy@acme.fi") parses as user "privacy" at host
  // "acme.fi" and would link to the domain root. A public policy page has no port either.
  if (url.username || url.password || url.port) return null

  // A domain name: two or more labels, and not an IP address ("0401234567" parses as one).
  const labels = url.hostname.split('.')
  if (labels.length < 2 || labels.includes('') || /^[\d.]+$/.test(url.hostname) || url.hostname.startsWith('[')) {
    return null
  }

  return href
}

/**
 * True when a valid privacy policy URL is the organization's front page ("acme.fi",
 * "https://www.acme.fi/") rather than the policy page. It is still stored, since a one-page site
 * can hold the policy, but the editors warn about it.
 *
 * The front page of a further subdomain is a site of its own and can be the policy
 * ("policies.barona.fi"); counting labels also misses "acme.co.uk", which only skips the warning. A
 * query or fragment on the root can select the page, e.g. WordPress's "?page_id=3".
 */
export function isFrontPageUrl(value: string): boolean {
  const href = toPrivacyPolicyHref(value)
  if (!href) return false
  const url = new URL(href)
  const labels = url.hostname.split('.')
  const siteLabels = labels[0] === 'www' ? labels.slice(1) : labels
  return url.pathname === '/' && !url.search && !url.hash && siteLabels.length <= 2
}

/** Normalize a stored privacy policy URL; an empty value stays empty, an invalid one is null. */
export function normalizePrivacyPolicyUrl(value: string): string | null {
  return value.trim() === '' ? '' : toPrivacyPolicyHref(value)
}
